import { Outlet, useLocation } from 'react-router-dom';
import { MarketingNav } from './MarketingNav';
import { MarketingFooter } from './MarketingFooter';
import { CookieBanner } from './CookieBanner';
import { RouteErrorBoundary } from './ErrorBoundary';
import { usePageView } from '../../lib/track';
import { useGa4 } from '../../lib/ga4';
import { useCanonical } from '../../lib/canonical';
import { useMarketingScroll } from './useMarketingScroll';

/**
 * Shared layout for all public marketing pages.
 * Provides a sticky nav, main content area, and footer.
 *
 * Usage: wrap marketing <Route> elements with this layout
 * via a parent <Route element={<MarketingLayout />}>.
 *
 * Also the single mount point for page-view tracking: every public route
 * renders through here, so one hook covers the whole marketing site and no
 * new page can be added without being counted.
 *
 * Both trackers mount here and nowhere else. usePageView() is the first-party,
 * cookieless beacon that always fires; useGa4() loads Google Analytics and only
 * after the visitor accepts analytics cookies. Keeping GA4 on this layout is
 * what stops it from ever seeing an authenticated path — dashboard URLs carry
 * client identifiers, which are not Google's business.
 *
 * The canonical URL is set here for the same reason: the marketing site is
 * also reachable on app. and www., and only routes rendered through this
 * layout should claim a canonical on waypoint-intel.com (src/lib/canonical.ts).
 *
 * Scroll position is owned here too (useMarketingScroll): the layout outlives
 * each page, so the window would otherwise carry one page's offset into the next.
 */
export function MarketingLayout({ children }: { children?: React.ReactNode }) {
  const { pathname } = useLocation();
  usePageView();
  useGa4();
  useCanonical();
  useMarketingScroll();
  return (
    <div className="flex min-h-screen flex-col bg-surface-0">
      <MarketingNav />
      <main className="flex-1">
        {/* Per-page boundary so a crash on a public page degrades gracefully
            (keyed by pathname so it resets when navigating between pages). */}
        <RouteErrorBoundary key={pathname}>{children ?? <Outlet />}</RouteErrorBoundary>
      </main>
      <MarketingFooter />
      <CookieBanner />
    </div>
  );
}
